import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { API_URL } from '../config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on initial load
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // For a simple token, use the structure that matches our worker.js format
        const tokenParts = token.split('.');
        if (tokenParts.length >= 2) {
          try {
            // Try normal JWT decode first
            const decoded = jwt_decode(token);
            setCurrentUserFromToken(decoded, token);
          } catch (jwtError) {
            // Fall back to our custom token format
            console.log("Standard JWT decode failed, trying custom format");
            try {
              const payload = JSON.parse(atob(tokenParts[1]));
              setCurrentUserFromToken(payload, token);
            } catch (e) {
              console.error("Failed to parse token payload:", e);
              handleTokenError();
            }
          }
        } else {
          handleTokenError();
        }
      } catch (error) {
        console.error("Token processing error:", error);
        handleTokenError();
      }
    } else {
      setLoading(false);
    }
  }, []);

  const setCurrentUserFromToken = (payload, token) => {
    // Check if token is expired
    const expiry = payload.exp * 1000;
    if (expiry < Date.now()) {
      console.log("Token expired, logging out");
      handleTokenError();
      return;
    }
    
    console.log("Valid token found, setting user");
    setCurrentUser({
      id: payload.userId || payload.id,
      name: payload.name,
      email: payload.email,
      role: payload.role
    });
    
    // Set default auth header for all requests
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setLoading(false);
  };

  const handleTokenError = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      console.log(`Attempting login for ${email} to ${API_URL}/auth/login`);
      
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      console.log("Login response:", response.data);
      
      if (response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        
        // Set user directly from the response
        setCurrentUser({
          id: response.data.user.id,
          name: response.data.user.name,
          email: response.data.user.email,
          role: response.data.user.role
        });
        
        // Set default auth header for subsequent requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        return { success: true };
      }
      
      return { 
        success: false, 
        message: 'Invalid response format from server' 
      };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to login. Please check your credentials.' 
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      console.log(`Attempting registration for ${email} to ${API_URL}/auth/register`);
      
      const response = await axios.post(`${API_URL}/auth/register`, { 
        name, 
        email, 
        password 
      });
      
      console.log("Registration response:", response.data);
      
      return { 
        success: response.data.success, 
        message: response.data.message || 'Registration successful!' 
      };
    } catch (error) {
      console.error('Register error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to register. Please try again.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
  };

  // Check if user is superadmin
  const isSuperAdmin = () => {
    return currentUser?.role === 'superadmin';
  };

  // Value to provide
  const value = {
    currentUser,
    login,
    register,
    logout,
    isSuperAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
